import React from 'react';
import { CountryCode } from '@dayetopia/types';
import {
  CLARE_MELLON_IMG,
  DEBORAH_MAQUIRE_IMG,
  DR_DONIERT_MCFARLANE_IMG,
  DR_LINDA_FARAHANI_IMG,
  SARAH_MONTAGU_IMG,
  DR_SHAZIA_MALIK_IMG,
  MARSHA_KENTISH_IMG,
  MELISSA_NASSANEY_IMG,
  NATASHA_EVANS_IMG,
  VIENNA_FARLOW_IMG,
  ZOC_DOC_IMG,
  DR_LINDA_FARAHANI_URL,
  DR_SHAZIA_MALIK_URL,
  DR_MELISSA_NASSANEY_URL,
  GP_BOOKING_URL,
  NATASHA_EVANS_BOOKING_URL,
  PPC_NURSE_CONSULTATION_URL,
  SEXUAL_HEALTH_NURSES_BOOKING_URL,
  SEXUAL_HEALTH_NURSES_FERTILITY_PREPAID_BOOKING_URL,
  SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL,
  US_SEXUAL_HEALTH_NURSES_BOOKING_URL,
  VMS_HEALTHCOUNTER_TERMS_URL,
  VMS_NUTRITIONIST_TERMS_URL,
  PLAN_YOUR_BABY_URL,
  PLAN_YOUR_BABY_IMG,
  ORIGIN_URL,
  ORIGIN_IMG,
  VIENNA_FARLOW_URL,
  MARSHA_KENTISH_URL,
  ZOCDOC_URL,
  IE_SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL
} from '@constants';
import { ProductCode, Credit, DigitalServiceType, PriceComplex, VitalsAuthorData } from '@contracts';
import FertilityLargeIcon from '@images/vms-account/ppc/fertility_large.svg';
import FertilityIcon from '@images/vms-account/ppc/fertility_small.svg';
import NursesLargeIcon from '@images/vms-account/ppc/nurses_large.svg';
import NursesIcon from '@images/vms-account/ppc/nurses_small.svg';
import NutritionistsLargeIcon from '@images/vms-account/ppc/nutritionists_large.svg';
import NutritionistsIcon from '@images/vms-account/ppc/nutritionists_small.svg';
import PelvicPainLargeIcon from '@images/vms-account/ppc/pelvicpain_large.svg';
import PelvicPainIcon from '@images/vms-account/ppc/pelvicpain_small.svg';
import Treatment from '@images/vms-account/vms-icons/Treatment.svg';
import { countriesInEULocale } from '@utils/country';
import { showInCountryCondition } from '@utils/country';

export interface SpecialistSection {
  title: string;
  id: SpecialistSectionId;
  description: string;
  helpWith?: string;
  icon: JSX.Element;
  smallIcon: JSX.Element;
  time?: string;
  price?: PriceComplex;
  productCode?: ProductCode;
  terms?: string;
  country?: string;
  url?: string;
  ppcUrl?: string;
  prepaidUrl?: string;
  externalBooking?: boolean;
  persons?: BookingPerson[];
  prepare?: string;
  split?: boolean;
}

export interface BookingPerson {
  name: string;
  title?: string;
  shortTitle?: string;
  picture: string;
  bio: string;
  shortBio?: string;
  country?: string;
  url?: string;
  price?: PriceComplex;
  productCode?: ProductCode;
  description?: string;
  shortDescription?: string;
  experience?: string;
  prepaidUrl?: string;
  time?: string;
  ppcOnly?: boolean;
  helpWithTags?: string[];
  prepare?: string;
  isOrganisation?: boolean;
  externalBooking?: boolean;
  showOnNursesProductPage?: boolean;
  showWhoAre?: boolean;
}

export interface SpecialistOptions {
  type: DigitalServiceType;
  country?: CountryCode;
  excludeSectionId?: SpecialistSectionId;
  includeSectionId?: SpecialistSectionId;
  names?: string[];
}

export enum SpecialistSectionId {
  GeneralPractitioners = 'general_practitioners',
  SexualReproductive = 'sexual_and_reproductive',
  OBGYNSpecialists = 'obgyn-specialisists',
  Fertility = 'fertility',
  Nutritionists = 'nutritionists',
  PeriodPelvicPain = 'period-pelvic-pain-specialisists',
  Other = 'other'
}

export const hasPrepaidConsultationCredit = (credits: Credit[], item: SpecialistSection, person?: BookingPerson) => {
  // Filter out credits that have a count of 0 to simplify further logic
  const validCredits = credits?.filter((credit) => credit.count > 0);

  // Determine if there is a matching credit based on the person's productCode, item's productCode, or specific product codes
  const hasMatchingCredit = (productCodes: ProductCode[]) =>
    validCredits.some(
      (credit) => productCodes.includes(credit.variant) || credit.variant === item?.productCode || credit.variant === person?.productCode
    );

  // Define product codes for each item id
  const productCodesMap = {
    [SpecialistSectionId.SexualReproductive]: [ProductCode.PPCAdvanced, ProductCode.VMSEssentialConsultation],
    [SpecialistSectionId.PeriodPelvicPain]: [ProductCode.PPCPremium],
    [SpecialistSectionId.GeneralPractitioners]: [],
    [SpecialistSectionId.OBGYNSpecialists]: [],
    [SpecialistSectionId.Fertility]: [],
    [SpecialistSectionId.Nutritionists]: [],
    [SpecialistSectionId.Other]: []
  };

  // Get the relevant product codes for the current item id, or an empty array if none are defined
  const relevantProductCodes = productCodesMap[item.id] || [];

  // Check for available credits based on item id and relevant product codes
  return hasMatchingCredit(relevantProductCodes);
};

export function getSpecialists(options: SpecialistOptions): SpecialistSection[] {
  const { country, excludeSectionId, includeSectionId, names, type } = options;
  const specialistsData = type === DigitalServiceType.PPC ? specialistsPPC : specialistsVMS;

  const mappedSpecialists = specialistsData.map((section) => {
    // Filter the persons inside each section based on names and country
    const filteredPersons = section.persons?.filter((person) => {
      const { name, country: personShowInCountries } = person;
      // If names are provided, only include the person if their name is in the list
      if (names && names.length && !names.includes(name)) {
        return false;
      }

      if (!personShowInCountries || showInCountryCondition(personShowInCountries, country)) {
        return true;
      }

      // In all other cases, we exclude the person
      return false;
    });

    return {
      ...section,
      persons: filteredPersons?.map((person) => ({ ...person, price: person?.price || section?.price }))
    };
  });

  let specialistSections = mappedSpecialists;

  if (excludeSectionId) {
    specialistSections = mappedSpecialists.filter((section) => {
      // If excludeSectionId is provided, exclude the section with that ID
      if (excludeSectionId && section.id === excludeSectionId) {
        return false;
      }
      return true;
    });
  }
  if (includeSectionId) {
    specialistSections = mappedSpecialists.filter((section) => {
      // If includeSectionId is provided, include only the section with that ID
      if (includeSectionId && section.id === includeSectionId) {
        return true;
      }
      return false;
    });
  }

  return specialistSections.filter((section) => {
    const { persons, country: sectionShowInCountries } = section;
    // If no persons left in the section, exclude it
    if (persons && persons.length === 0) {
      return false;
    }

    if (!sectionShowInCountries || showInCountryCondition(sectionShowInCountries, country)) {
      return true;
    }

    // In all other cases, we exclude the section
    return false;
  });
}

export function getSpecialistsByName(type: DigitalServiceType, names: string[], country = CountryCode.GB): BookingPerson[] {
  let allPersons: BookingPerson[] = [];

  getSpecialists({ names, type, country }).forEach((section) => {
    if (section.persons) {
      allPersons = [...allPersons, ...section.persons.map((person) => ({ productCode: section?.productCode, ...person }))];
    }
  });

  return names.map((name) => allPersons.find((person) => person.name === name) as BookingPerson);
}

/**
 * Retrieves specialists for the product page based on type, country, and optional category.
 * Only returns specialists with showOnNursesProductPage flag set to true.
 *
 * @param {DigitalServiceType} type - The type of digital service (e.g., PPC, VMS)
 * @param {CountryCode} country - The country code, defaults to GB
 * @param {SpecialistSection} [category] - Optional category to filter specialists by section
 * @returns {BookingPerson[]} Array of booking persons filtered by the given criteria
 */
export function getSpecialistsForProductPage(type: DigitalServiceType, country = CountryCode.GB, category?: SpecialistSectionId): BookingPerson[] {
  let allPersons: BookingPerson[] = [];

  const sections = getSpecialists({ type, country });
  const relevantSections = category ? sections.filter((s) => s.id === category) : sections;

  relevantSections.forEach((section) => {
    if (section.persons) {
      allPersons = [
        ...allPersons,
        ...section.persons.map((person) => ({
          productCode: section?.productCode,
          ...person
        }))
      ];
    }
  });

  return allPersons.filter((person) => person.showOnNursesProductPage);
}

/**
 * Extracts the first name from a BookingPerson object.
 * If the first word of the name is 'Dr', it assumes the actual first name is the second word.
 * This is useful for displaying only the first name in contexts where a more personal touch is desired or where space is limited.
 *
 * @param {BookingPerson} person - The BookingPerson object containing the full name.
 * @returns {string} The extracted first name of the person, including 'Dr' if contained in the full name.
 */
export function getFirstName(person: BookingPerson | VitalsAuthorData): string {
  if (!person.name) {
    return 'They';
  }
  const firstNamePart = person.name.split(' ')[0];
  const lastNamePart = person.name.split(' ')[1];
  const renderName = firstNamePart === 'Dr' ? `Dr ${lastNamePart}` : firstNamePart;
  return renderName;
}

const specialistsPPC: SpecialistSection[] = [
  {
    title: 'Sexual health nurses',
    id: SpecialistSectionId.SexualReproductive,
    description: 'Our nurses are highly qualified and can help you get a better understanding of your screening results and what they could mean.',
    helpWith:
      'Our highly qualified nurses are here to help you understand your report — the symptoms of different conditions, pain relief and contraception options, as well as give you general advice on sexual health. They’ll make sure you feel equipped and confident to take the next step on your diagnostic journey, but can’t provide a diagnosis themselves.',
    icon: <NursesLargeIcon />,
    smallIcon: <NursesIcon />,
    time: '30 min',
    price: '29.99',
    country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
    url: SEXUAL_HEALTH_NURSES_BOOKING_URL,
    ppcUrl: PPC_NURSE_CONSULTATION_URL,
    prepaidUrl: SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL,
    persons: [
      {
        name: 'Sarah Montagu',
        title: 'RN DFSRH, BSc',
        shortTitle: 'RN DFSRH BSc',
        description: 'Sexual Health Nurse',
        picture: SARAH_MONTAGU_IMG,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
        experience: '15+ years of experience',
        helpWithTags: ['Sexually transmitted infections', 'Recurrent infections', 'Contraception', 'Menopause'],
        bio: 'Sarah, a seasoned contraception and sexual health nurse with over 15 years of experience, specialises in managing sexual health clinics. She is dedicated to reducing stigma around contraception and sexual health by sharing evidence-based information.'
      },
      {
        name: 'Deborah Maguire',
        title: 'RN, BSc',
        shortTitle: 'RN, BSc',
        shortDescription: 'Fertility Nurse',
        description: 'Sexual Health Nurse',
        picture: DEBORAH_MAQUIRE_IMG,
        prepaidUrl: SEXUAL_HEALTH_NURSES_FERTILITY_PREPAID_BOOKING_URL,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
        time: '30 min',
        experience: '10+ years of experience',
        helpWithTags: ['Reproductive health', 'Fertility', 'IVF', 'Donor conception', 'Vaginal Infections', 'PCOS', 'Hormonal health'],
        bio: "Meet Deborah, a women's health nurse with over a decade of experience in gynaecology care, fertility and early pregnancy management. If you're navigating vaginal infections, fertility challenges, pregnancy, or simply seeking advice on nutrition and hormone health, Deborah is here to help every step of the way.",
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:deborah@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">deborah@yourdaye.com</a>`
      }
    ]
  },
  {
    title: 'Sexual health coaches',
    id: SpecialistSectionId.SexualReproductive,
    description: 'Our coaches are highly qualified and can help you get a better understanding of your screening results and what they could mean.',
    helpWith:
      'Our highly qualified coaches are here to help you understand your report — the symptoms of different conditions, pain relief and contraception options, as well as give you general advice on sexual health. They’ll make sure you feel equipped and confident to take the next step on your diagnostic journey, but can’t provide a diagnosis themselves.',
    icon: <NursesLargeIcon />,
    smallIcon: <NursesIcon />,
    time: '30 min',
    price: '29.99',
    country: `${CountryCode.IE}`,
    url: SEXUAL_HEALTH_NURSES_BOOKING_URL,
    ppcUrl: PPC_NURSE_CONSULTATION_URL,
    prepaidUrl: IE_SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL,
    persons: [
      {
        name: 'Sarah Montagu',
        title: 'RN DFSRH, BSc',
        shortTitle: 'RN DFSRH BSc',
        description: 'Sexual Health Coach',
        picture: SARAH_MONTAGU_IMG,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.IE}`,
        experience: '15+ years of experience',
        helpWithTags: ['Sexually transmitted infections', 'Recurrent infections', 'Contraception', 'Menopause'],
        bio: 'Sarah, a seasoned contraception and sexual health coach with over 15 years of experience, specialises in managing sexual health clinics. She is dedicated to reducing stigma around contraception and sexual health by sharing evidence-based information.'
      },
      {
        name: 'Deborah Maguire',
        title: 'RN, BSc',
        shortTitle: 'RN, BSc',
        shortDescription: 'Fertility Coach',
        description: 'Sexual Health Coach',
        picture: DEBORAH_MAQUIRE_IMG,
        prepaidUrl: IE_SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.IE}`,
        time: '30 min',
        experience: '10+ years of experience',
        helpWithTags: ['Reproductive health', 'Fertility', 'IVF', 'Donor conception', 'Vaginal Infections', 'PCOS', 'Hormonal health'],
        bio: "Meet Deborah, a women's health coach with over a decade of experience in gynaecology care, fertility and early pregnancy management. If you're navigating vaginal infections, fertility challenges, pregnancy, or simply seeking advice on nutrition and hormone health, Deborah is here to help every step of the way.",
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:deborah@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">deborah@yourdaye.com</a>`
      }
    ]
  },
  {
    title: 'Gynae health experts',
    id: SpecialistSectionId.SexualReproductive,
    description: 'Our coaches are highly qualified and can help you get a better understanding of your screening results and what they could mean.',
    helpWith:
      'Our highly qualified nurses are here to help you understand your report — the symptoms of different conditions, pain relief and contraception options, as well as give you general advice on sexual health. They’ll make sure you feel equipped and confident to take the next step on your diagnostic journey, but can’t provide a diagnosis themselves.',
    icon: <NursesLargeIcon />,
    smallIcon: <NursesIcon />,
    time: '30 min',
    country: `${CountryCode.US}`,
    price: '55.00',
    split: true,
    productCode: ProductCode.ConsultationSexualHealth,
    persons: [
      {
        name: 'Marsha Kentish',
        title: 'RN, CNS, LDN',
        picture: MARSHA_KENTISH_IMG,
        time: '30 min',
        price: '55.00',
        country: `${CountryCode.US}`,
        url: MARSHA_KENTISH_URL,
        prepaidUrl: MARSHA_KENTISH_URL,
        bio: "Marsha Kentish is a Women's Health Coach with a wealth of experience in managing chronic conditions through evidence-based and holistic approaches. As a licensed Dietitian and Nurse consultant, she is dedicated to improving the health and well-being of women, with a particular focus on addressing hormonal imbalances, chronic pain, mental health, gut disorders, and gynaecological issues.",
        helpWithTags: ['Recurring infections', 'Menopause', 'Hormonal health', 'PCOS', 'Pelvic pain'],
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:marsha@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">marsha@yourdaye.com</a>`
      },
      {
        name: 'Vienna Farlow',
        shortDescription: 'Fertility Coach',
        title: 'Fertility Coach',
        picture: VIENNA_FARLOW_IMG,
        time: '30 min',
        country: `${CountryCode.US}`,
        price: '55.00',
        url: VIENNA_FARLOW_URL,
        prepaidUrl: VIENNA_FARLOW_URL,
        bio: 'Vienna Farlow is a Holistic Reproductive Health Practitioner and certified Justisse Method Fertility Awareness Educator with over seven years of experience working with individuals and couples. She specializes in menstrual cycle health, fertility, and reproductive and sexual wellness. Her work is focused on demystifying the menstrual cycle and empowering all menstruators to connect with their bodies in new and deeper ways.',
        helpWithTags: ['Endometriosis', 'PCOS', 'Fertility Awareness', 'Trying to conceive', 'Irregular Cycles', 'Period pain'],
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:vienna.farlow@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">vienna.farlow@yourdaye.com</a>`
      },
      {
        name: 'Melissa Nassaney',
        shortDescription: 'Pelvic Coach',
        title: 'Pelvic Coach',
        picture: MELISSA_NASSANEY_IMG,
        time: '30 min',
        country: `${CountryCode.US}`,
        price: '45.00',
        productCode: ProductCode.ConsultationPelvicFloorTherapist,
        url: DR_MELISSA_NASSANEY_URL,
        externalBooking: true,
        bio: 'Dr. Melissa Nassaney is a Pelvic Floor Coach with over 30 years of experience in treating pelvic floor disorders. Her practice integrates yoga and movement to foster mind-body connections and self-awareness. She supports patients in early recognition of pelvic floor issues to optimize pelvic health and improve overall outcomes and wellbeing.',
        helpWithTags: ['Pelvic pain', 'Pain during sex', 'Constipation', 'Back pain', 'Urinary urgency/frequency'],
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a href="mailto:fullcirclepelvicpt@gmail.com?subject=Daye%20Screening%20results%20for%20consultation">fullcirclepelvicpt@gmail.com</a>`
      }
    ]
  },
  {
    title: 'General practitioners',
    id: SpecialistSectionId.GeneralPractitioners,
    description: 'GPs can help diagnose common gynaecological health conditions.',
    icon: <Treatment />,
    smallIcon: <Treatment />,
    time: '15 min',
    price: '44.99',
    terms: VMS_HEALTHCOUNTER_TERMS_URL,
    url: GP_BOOKING_URL,
    productCode: ProductCode.ConsultationGeneralPractitioner,
    country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
    persons: [
      {
        name: 'Dr Doniert Macfarlane',
        title: 'MBBS BSc MRCS',
        picture: DR_DONIERT_MCFARLANE_IMG,
        productCode: ProductCode.ConsultationGeneralPractitioner,
        helpWithTags: ['BV', 'Thrush'],
        bio: 'Dr Macfarlane is a general practitioner with over 15 years of experience in healthcare and telemedicine.'
      }
    ],
    prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:doniertmac@gmail.com?subject=Daye%20Screening%20results%20for%20consultation">doniertmac@gmail.com</a>`
  },
  {
    title: 'Period & pelvic pain specialists',
    id: SpecialistSectionId.OBGYNSpecialists,
    description: 'Find vetted healthcare providers who can help you get answers and manage your pelvic pain.',
    helpWith:
      'Our trusted specialists can help to investigate your pain, and can provide you with a proper diagnosis. They may need additional tests (like ultrasound, blood tests, or others) to investigate your pain.',
    icon: <PelvicPainLargeIcon />,
    smallIcon: <PelvicPainIcon />,
    split: true,
    country: `${CountryCode.US}`,
    persons: [
      {
        name: 'Origin Physiotherapy',
        title: 'PT Clinic',
        shortTitle: 'PT Clinic',
        picture: ORIGIN_IMG,
        url: ORIGIN_URL,
        externalBooking: true,
        helpWithTags: [
          'Ureaplasma treatments',
          'IVF',
          'Egg freezing',
          'Recurrent miscarriage',
          'Recurrent miscarriage',
          'Pregnancy monitoring',
          'Non-invasive fertility treatment '
        ],
        isOrganisation: true,
        bio: 'Origin is the nationwide leader in pelvic floor and women’s health physical therapy, with virtual care in 50 states, 17 clinics, and 41M covered lives. We support full body needs for women and all people with vaginal anatomy through every stage of life, serving over 30,000 patients to date with an NPS of 98.\n\n Pelvic floor dysfunction (PFD) and its related symptoms impact the vast majority of women. Unaddressed, PFD costs the healthcare system billions of dollars and has an outsized impact on physical and mental health.\n\n We’re on a mission to create a higher standard of care and increase access to this essential yet missing piece of women’s health. For more information, please visit <a href="https://www.theoriginway.com/">www.theoriginway.com</a> or <a href="https://www.instagram.com/theoriginway/">@theoriginway.</a>'
      },
      {
        name: 'Book with ZocDoc',
        picture: ZOC_DOC_IMG,
        isOrganisation: true,
        bio: 'You can find hundreds of vetted specialists in the ZocDoc network.',
        url: ZOCDOC_URL,
        externalBooking: true,
        showWhoAre: false
      }
    ]
  },
  {
    title: 'Period & pelvic pain specialists',
    id: SpecialistSectionId.PeriodPelvicPain,
    description: 'You can get the opinion of experienced OBGYNs, who have spent years specialising in the gynaecological health field.',
    helpWith:
      'Our trusted specialists can help to investigate your pain, and can provide you with a proper diagnosis. They may need additional tests (like ultrasound, blood tests, or others) to investigate your pain.',
    icon: <PelvicPainLargeIcon />,
    smallIcon: <PelvicPainIcon />,
    url: 'https://www.clairemellon.co.uk/gynaecology',
    externalBooking: true,
    country: `${CountryCode.GB}`,
    persons: [
      {
        name: 'Miss Claire Mellon & Associates',
        picture: CLARE_MELLON_IMG,
        isOrganisation: true,
        bio: 'The clinic offers a broad range of services, including general check ups, management of gynaecological conditions and the menopause, fertility check-ups, early pregnancy assessments and sexual health checks.'
      },
      {
        name: 'Dr Shazia Malik',
        title: 'MBChB (Honours), MRCOG',
        shortTitle: 'MBChB (Honours), MRCOG',
        shortDescription: 'OB-GYN',
        picture: DR_SHAZIA_MALIK_IMG,
        description: 'Reproductive Medicine Specialist',
        experience: '30+ years of experience',
        url: DR_SHAZIA_MALIK_URL,
        helpWithTags: ['Fertility', 'Miscarriage', 'Pregnancy', 'PCOS', 'Menopause', 'Ureaplasma infections'],
        bio: 'Dr. Shazia Malik is an OBGYN with more than 30 years of experience in helping patients with pregnancy and fertility concerns. As a specialist in Reproductive Medicine, she supports patients through IVF treatments, high-risk pregnancies, recurrent miscarriages, menopause, complex vaginal infections and other gynaecological conditions, such as Endometriosis or PCOS.'
      }
    ]
  },
  {
    title: 'Fertility specialists',
    id: SpecialistSectionId.Fertility,
    description: 'Connect with vetted experts on fertility, IVF and reproductive health.',
    helpWith:
      'Our partner specialists can provide you with a detailed understanding of your fertility and pregnancy history, offer personalised medical support to progress your journey towards conception, and diagnose any underlying gynae health conditions.',
    icon: <FertilityLargeIcon />,
    smallIcon: <FertilityIcon />,
    split: true,
    country: `${CountryCode.GB}`,
    persons: [
      {
        name: 'Dr Linda Farahani',
        title: 'MBBS BSc PGCert',
        shortTitle: 'MBBS BSc PGCert',
        picture: DR_LINDA_FARAHANI_IMG,
        url: DR_LINDA_FARAHANI_URL,
        time: '30 min',
        price: '174.99',
        externalBooking: true,
        description: 'OBGYN & pelvic pain specialist',
        experience: '20+ years of experience',
        helpWithTags: ['Fertility', 'Miscarriage', 'IVF', 'PCOS', 'Endometriosis'],
        bio: 'Dr. Farahani is an OBGYN with 20 years of experience in PCOS, infertility, IVF and recurrent pregnancy loss. She is an active researcher in the field of  reproductive health and the microbiome.'
      },
      {
        name: 'Plan your Baby',
        title: 'Fertility Clinic',
        shortTitle: 'Fertility Clinic',
        picture: PLAN_YOUR_BABY_IMG,
        url: PLAN_YOUR_BABY_URL,
        time: '30 min',
        price: '125.99',
        externalBooking: true,
        helpWithTags: [
          'Ureaplasma treatments',
          'IVF',
          'Egg freezing',
          'Recurrent miscarriage',
          'Recurrent miscarriage',
          'Pregnancy monitoring',
          'Non-invasive fertility treatment '
        ],
        isOrganisation: true,
        bio: 'Plan your Baby is your go-to clinic, delivering virtual and close to home end-to-end fertility and pregnancy solutions, for couples and individuals.'
      }
    ]
  },
  {
    title: 'Nutrition and physical therapy',
    id: SpecialistSectionId.Nutritionists,
    description:
      'Nutrition can play a key role in the wellbeing of your microbiomes. That’s why we’re partnering with nutrition therapists who can help you boost your diet.',
    helpWith:
      'Learn how your diet and lifestyle habits like activity, stress, and sleep might be impacting your pain, and get get practical, personalised advise on changes you can make to manage your symptoms and improve your quality of life.',
    icon: <NutritionistsLargeIcon />,
    smallIcon: <NutritionistsIcon />,
    time: '45 min',
    terms: VMS_NUTRITIONIST_TERMS_URL,
    split: true,
    country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
    persons: [
      {
        name: 'Natasha Evans',
        title: 'DipION mBANT rCNHC',
        shortTitle: 'DipION mBANT rCNHC',
        description: 'Nutritional Specialist',
        experience: '10+ years of experience',
        picture: NATASHA_EVANS_IMG,
        url: NATASHA_EVANS_BOOKING_URL,
        productCode: ProductCode.ConsultationNutritionist,
        price: '84.99',
        helpWithTags: ['Fertility', 'IVF prep', 'Gut health', 'Recurrent thrush & BV', 'Hormonal health'],
        bio: 'Get holistic, evidence-based recommendations for your diet, lifestyle and environment.'
      }
    ],
    prepare: `In the 3 days before your session, please fill out <a to="https://www.penocch.io/t/9eda/">this food & lifestyle diary</a>. Please also <a to="https://www.penocch.io/u/b73a/">upload your screening results PDF here</a>.`
  }
];

const specialistsVMS: SpecialistSection[] = [
  {
    title: 'Sexual and reproductive health nurses',
    id: SpecialistSectionId.SexualReproductive,
    description: 'Our nurses are highly qualified and can help you get a better understanding of your screening results and what they could mean.',
    helpWith:
      'Our highly qualified nurses are here to help you understand your report — the symptoms of different conditions, pain relief and contraception options, as well as give you general advice on sexual health. They’ll make sure you feel equipped and confident to take the next step on your diagnostic journey, but can’t provide a diagnosis themselves.',
    icon: <NursesLargeIcon />,
    smallIcon: <NursesIcon />,
    time: '30 min',
    price: '29.99',
    country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
    url: SEXUAL_HEALTH_NURSES_BOOKING_URL,
    ppcUrl: PPC_NURSE_CONSULTATION_URL,
    split: true,
    persons: [
      {
        name: 'Sarah Montagu',
        title: 'RN DFSRH, BSc',
        shortTitle: 'RN DFSRH BSc',
        shortDescription: 'Sexual Health Nurse',
        description: 'Sexual health nurse',
        picture: SARAH_MONTAGU_IMG,
        prepaidUrl: SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
        showOnNursesProductPage: true,
        time: '30 min',
        experience: '15+ years of experience',
        helpWithTags: ['Sexually transmitted infections', 'Recurrent infections', 'Contraception', 'Menopause'],
        bio: 'Meet Sarah, our sexual & reproductive health nurse with over 15 years of expertise in the vaginal microbiome, recurrent infections, STIs, contraception, and menopause. If you struggle with any of these, she is here to help you find the root cause and give you the support and personalised approach you need to take control of your gynae health.',
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:sarah@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">sarah@yourdaye.com</a>`
      },
      {
        name: 'Deborah Maguire',
        title: 'RN, BSc',
        shortTitle: 'RN, BSc',
        shortDescription: 'Fertility Nurse',
        description: 'Sexual health nurse',
        picture: DEBORAH_MAQUIRE_IMG,
        prepaidUrl: SEXUAL_HEALTH_NURSES_FERTILITY_PREPAID_BOOKING_URL,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
        showOnNursesProductPage: true,
        time: '30 min',
        experience: '10+ years of experience',
        helpWithTags: ['Reproductive health', 'Fertility', 'IVF', 'Donor conception', 'Vaginal Infections', 'PCOS', 'Hormonal health'],
        bio: "Meet Deborah, a women's health nurse with over a decade of experience in gynaecology care, fertility and early pregnancy management. If you're navigating vaginal infections, fertility challenges, pregnancy, or simply seeking advice on nutrition and hormone health, Deborah is here to help every step of the way.",
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:deborah@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">deborah@yourdaye.com</a>`
      }
    ]
  },
  {
    title: 'Sexual and reproductive health coaches',
    id: SpecialistSectionId.SexualReproductive,
    description: 'Our coaches are highly qualified and can help you get a better understanding of your screening results and what they could mean.',
    helpWith:
      'Our highly qualified coaches are here to help you understand your report — the symptoms of different conditions, pain relief and contraception options, as well as give you general advice on sexual health. They’ll make sure you feel equipped and confident to take the next step on your diagnostic journey, but can’t provide a diagnosis themselves.',
    icon: <NursesLargeIcon />,
    smallIcon: <NursesIcon />,
    time: '30 min',
    price: '29.99',
    country: `${CountryCode.IE}`,
    url: SEXUAL_HEALTH_NURSES_BOOKING_URL,
    ppcUrl: PPC_NURSE_CONSULTATION_URL,
    split: true,
    persons: [
      {
        name: 'Sarah Montagu',
        title: 'RN DFSRH, BSc',
        shortTitle: 'RN DFSRH BSc',
        shortDescription: 'Sexual Health Coach',
        description: 'Sexual Health Coach',
        picture: SARAH_MONTAGU_IMG,
        prepaidUrl: IE_SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.IE}`,
        showOnNursesProductPage: true,
        time: '30 min',
        experience: '15+ years of experience',
        helpWithTags: ['Sexually transmitted infections', 'Recurrent infections', 'Contraception', 'Menopause'],
        bio: 'Meet Sarah, our sexual & reproductive health coach with over 15 years of expertise in the vaginal microbiome, recurrent infections, STIs, contraception, and menopause. If you struggle with any of these, she is here to help you find the root cause and give you the support and personalised approach you need to take control of your gynae health.',
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:sarah@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">sarah@yourdaye.com</a>`
      },
      {
        name: 'Deborah Maguire',
        title: 'RN, BSc',
        shortTitle: 'RN, BSc',
        shortDescription: 'Fertility Coach',
        description: 'Sexual Health Coach',
        picture: DEBORAH_MAQUIRE_IMG,
        prepaidUrl: IE_SEXUAL_HEALTH_NURSES_PREPAID_BOOKING_URL,
        productCode: ProductCode.ConsultationSexualHealth,
        country: `${CountryCode.IE}`,
        showOnNursesProductPage: true,
        time: '30 min',
        experience: '10+ years of experience',
        helpWithTags: ['Reproductive health', 'Fertility', 'IVF', 'Donor conception', 'Vaginal Infections', 'PCOS', 'Hormonal health'],
        bio: "Meet Deborah, a women's health coach with over a decade of experience in gynaecology care, fertility and early pregnancy management. If you're navigating vaginal infections, fertility challenges, pregnancy, or simply seeking advice on nutrition and hormone health, Deborah is here to help every step of the way.",
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:deborah@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">deborah@yourdaye.com</a>`
      }
    ]
  },
  {
    title: 'Gynae Health experts',
    id: SpecialistSectionId.SexualReproductive,
    description: 'Our nurses are highly qualified and can help you get a better understanding of your screening results and what they could mean.',
    helpWith:
      'Our highly qualified nurses are here to help you understand your report — the symptoms of different conditions, pain relief and contraception options, as well as give you general advice on sexual health. They’ll make sure you feel equipped and confident to take the next step on your diagnostic journey, but can’t provide a diagnosis themselves.',
    icon: <NursesLargeIcon />,
    smallIcon: <NursesIcon />,
    time: '30 min',
    country: `${CountryCode.US}`,
    price: '55.00',
    split: true,
    productCode: ProductCode.ConsultationSexualHealth,
    url: US_SEXUAL_HEALTH_NURSES_BOOKING_URL,
    ppcUrl: US_SEXUAL_HEALTH_NURSES_BOOKING_URL,

    persons: [
      {
        name: 'Marsha Kentish',
        title: 'RN, CNS, LDN',
        description: 'Sexual Health Coach',
        shortDescription: 'Sexual Health Coach',
        picture: MARSHA_KENTISH_IMG,
        time: '30 min',
        country: `${CountryCode.US}`,
        showOnNursesProductPage: true,
        price: '55.00',
        url: MARSHA_KENTISH_URL,
        prepaidUrl: MARSHA_KENTISH_URL,
        bio: "Marsha Kentish is a Women's Health Coach with a wealth of experience in managing chronic conditions through evidence-based and holistic approaches. As a licensed Dietitian and Nurse consultant, she is dedicated to improving the health and well-being of women, with a particular focus on addressing hormonal imbalances, chronic pain, mental health, gut disorders, and gynaecological issues.",
        helpWithTags: ['Recurring infections', 'Menopause', 'Hormonal health', 'PCOS', 'Pelvic pain'],
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:marsha@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">marsha@yourdaye.com</a>`
      },
      {
        name: 'Vienna Farlow',
        description: 'Fertility Coach',
        shortDescription: 'Fertility Coach',
        picture: VIENNA_FARLOW_IMG,
        time: '30 min',
        country: `${CountryCode.US}`,
        showOnNursesProductPage: true,
        price: '55.00',
        url: VIENNA_FARLOW_URL,
        prepaidUrl: VIENNA_FARLOW_URL,
        bio: 'Vienna Farlow is a Holistic Reproductive Health Practitioner and certified Justisse Method Fertility Awareness Educator with over seven years of experience working with individuals and couples. She specializes in menstrual cycle health, fertility, and reproductive and sexual wellness. Her work is focused on demystifying the menstrual cycle and empowering all menstruators to connect with their bodies in new and deeper ways.',
        helpWithTags: ['Endometriosis', 'PCOS', 'Fertility Awareness', 'Trying to conceive', 'Irregular Cycles', 'Period pain'],
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a to="mailto:vienna.farlow@yourdaye.com?subject=Daye%20Screening%20results%20for%20consultation">vienna.farlow@yourdaye.com</a>`
      },
      {
        name: 'Melissa Nassaney',
        shortDescription: 'Pelvic Coach',
        description: 'Pelvic Coach',
        picture: MELISSA_NASSANEY_IMG,
        time: '30 min',
        country: `${CountryCode.US}`,
        price: '45.00',
        externalBooking: true,
        url: DR_MELISSA_NASSANEY_URL,
        bio: 'Dr. Melissa Nassaney is a Pelvic Floor Coach with over 30 years of experience in treating pelvic floor disorders. Her practice integrates yoga and movement to foster mind-body connections and self-awareness. She supports patients in early recognition of pelvic floor issues to optimize pelvic health and improve overall outcomes and wellbeing.',
        helpWithTags: ['Pelvic pain', 'Pain during sex', 'Constipation', 'Back pain', 'Urinary urgency/frequency'],
        prepare: `After you book your consultation, don’t forget to send your screening results PDF to <a href="mailto:fullcirclepelvicpt@gmail.com?subject=Daye%20Screening%20results%20for%20consultation">fullcirclepelvicpt@gmail.com</a>`
      }
    ]
  },
  {
    title: 'Highly-specialised OBGYNs',
    id: SpecialistSectionId.OBGYNSpecialists,
    description: 'Find vetted healthcare providers who can help you get answers and manage your pelvic pain.',
    helpWith:
      'Our trusted specialists can help to investigate your pain, and can provide you with a proper diagnosis. They may need additional tests (like ultrasound, blood tests, or others) to investigate your pain.',
    icon: <PelvicPainLargeIcon />,
    smallIcon: <PelvicPainIcon />,
    url: ZOCDOC_URL,
    externalBooking: true,
    country: `${CountryCode.US}`,
    persons: [
      {
        name: 'Book with ZocDoc',
        picture: ZOC_DOC_IMG,
        isOrganisation: true,
        bio: 'You can find hundreds of vetted specialists in the ZocDoc network.',
        showWhoAre: false
      }
    ]
  },
  {
    title: 'Specialised OBGYNs',
    id: SpecialistSectionId.OBGYNSpecialists,
    description: 'You can get the opinion of experienced OBGYNs, who have spent years specialising in the gynaecological health field.',
    helpWith:
      'Our trusted specialists can help to investigate your pain, and can provide you with a proper diagnosis. They may need additional tests (like ultrasound, blood tests, or others) to investigate your pain.',
    icon: <PelvicPainLargeIcon />,
    smallIcon: <PelvicPainIcon />,
    externalBooking: true,
    split: true,
    country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
    persons: [
      {
        name: 'Miss Claire Mellon & Associates',
        picture: CLARE_MELLON_IMG,
        url: 'https://www.clairemellon.co.uk/gynaecology',
        isOrganisation: true,
        bio: 'The clinic offers a broad range of services, including general check ups, management of gynaecological conditions and the menopause, fertility check-ups, early pregnancy assessments and sexual health checks.'
      },
      {
        name: 'Dr Linda Farahani',
        title: 'MBBS BSc PGCert',
        shortTitle: 'MBBS BSc PGCert',
        shortDescription: 'OB-GYN',
        picture: DR_LINDA_FARAHANI_IMG,
        description: 'OBGYN & pelvic pain specialist',
        experience: '20+ years of experience',
        url: DR_LINDA_FARAHANI_URL,
        helpWithTags: ['Fertility', 'Miscarriage', 'IVF', 'PCOS', 'Endometriosis'],
        bio: 'Dr. Farahani is an OBGYN with 20 years of experience in PCOS, infertility, IVF and recurrent pregnancy loss. She is an active researcher in the field of  reproductive health and the microbiome.'
      },
      {
        name: 'Dr Shazia Malik',
        title: 'MBChB (Honours), MRCOG',
        shortTitle: 'MBChB (Honours), MRCOG',
        shortDescription: 'OB-GYN',
        picture: DR_SHAZIA_MALIK_IMG,
        description: 'Reproductive Medicine Specialist',
        experience: '30+ years of experience',
        url: DR_SHAZIA_MALIK_URL,
        helpWithTags: ['Fertility', 'Miscarriage', 'Pregnancy', 'PCOS', 'Menopause', 'Ureaplasma infections'],
        bio: 'Dr. Shazia Malik is an OBGYN with more than 30 years of experience in helping patients with pregnancy and fertility concerns. As a specialist in Reproductive Medicine, she supports patients through IVF treatments, high-risk pregnancies, recurrent miscarriages, menopause, complex vaginal infections and other gynaecological conditions, such as Endometriosis or PCOS.'
      }
    ]
  },
  {
    title: 'Nutritionists',
    id: SpecialistSectionId.Nutritionists,
    description:
      'Nutrition can play a key role in the wellbeing of your microbiomes. That’s why we’re partnering with nutrition therapists who can help you boost your diet.',
    helpWith:
      'Learn how your diet and lifestyle habits like activity, stress, and sleep might be impacting your pain, and get get practical, personalised advise on changes you can make to manage your symptoms and improve your quality of life.',
    icon: <NutritionistsLargeIcon />,
    smallIcon: <NutritionistsIcon />,
    terms: VMS_NUTRITIONIST_TERMS_URL,
    split: true,
    country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
    persons: [
      {
        name: 'Natasha Evans',
        title: 'DipION mBANT rCNHC',
        shortTitle: 'DipION mBANT rCNHC',
        shortDescription: 'Nutrition Specialist',
        description: 'Nutritional therapist & lifestyle coach',
        experience: '10+ years of experience',
        picture: NATASHA_EVANS_IMG,
        url: NATASHA_EVANS_BOOKING_URL,
        productCode: ProductCode.ConsultationNutritionist,
        time: '45 min',
        price: '84.99',
        helpWithTags: ['Fertility', 'IVF', 'Gut health', 'Recurrent thrush & BV', 'Hormonal health'],
        bio: 'Get holistic, evidence-based recommendations for your diet, lifestyle and environment.'
      }
    ],
    prepare: `In the 3 days before your session, please fill out <a to="https://www.penocch.io/t/9eda/">this food & lifestyle diary</a>. Please also <a to="https://www.penocch.io/u/b73a/">upload your screening results PDF here</a>.`
  },
  {
    title: 'Other',
    id: SpecialistSectionId.Other,
    description: 'Partners and experts from other categories.',
    helpWith: '',
    icon: <NutritionistsLargeIcon />,
    smallIcon: <NutritionistsIcon />,
    terms: VMS_NUTRITIONIST_TERMS_URL,
    split: true,
    country: `${CountryCode.GB}, ${countriesInEULocale.join(', ')}, ${CountryCode.DE}, ${CountryCode.BG}`,
    persons: [
      {
        name: 'Plan your Baby',
        title: 'Fertility Clinic',
        shortTitle: 'Fertility Clinic',
        shortDescription: 'Fertility Clinic',
        picture: PLAN_YOUR_BABY_IMG,
        url: PLAN_YOUR_BABY_URL,
        time: '30 min',
        price: '125.99',
        productCode: ProductCode.ConsultationPelvicFloorTherapist,
        externalBooking: true,
        helpWithTags: [
          'Ureaplasma treatments',
          'IVF',
          'Egg freezing',
          'Recurrent miscarriage',
          'Recurrent miscarriage',
          'Pregnancy monitoring',
          'Non-invasive fertility treatment '
        ],
        isOrganisation: true,
        bio: 'Plan your Baby is your go-to clinic, delivering virtual and close to home end-to-end fertility and pregnancy solutions, for couples and individuals.'
      }
    ]
  },
  {
    title: 'Other',
    id: SpecialistSectionId.Other,
    description: 'Partners and experts from other categories.',
    helpWith: '',
    icon: <NutritionistsLargeIcon />,
    smallIcon: <NutritionistsIcon />,
    terms: VMS_NUTRITIONIST_TERMS_URL,
    split: true,
    country: `${CountryCode.US}`,
    persons: [
      {
        name: 'Origin Physiotherapy',
        title: 'PT Clinic',
        shortDescription: 'PT Clinic',
        picture: ORIGIN_IMG,
        url: ORIGIN_URL,
        externalBooking: true,
        helpWithTags: [
          'Ureaplasma treatments',
          'IVF',
          'Egg freezing',
          'Recurrent miscarriage',
          'Recurrent miscarriage',
          'Pregnancy monitoring',
          'Non-invasive fertility treatment '
        ],
        isOrganisation: true,
        bio: 'Origin is the nationwide leader in pelvic floor and women’s health physical therapy, with virtual care in 50 states, 17 clinics, and 41M covered lives. We support full body needs for women and all people with vaginal anatomy through every stage of life, serving over 30,000 patients to date with an NPS of 98.\n\n Pelvic floor dysfunction (PFD) and its related symptoms impact the vast majority of women. Unaddressed, PFD costs the healthcare system billions of dollars and has an outsized impact on physical and mental health.\n\n We’re on a mission to create a higher standard of care and increase access to this essential yet missing piece of women’s health. For more information, please visit <a href="https://www.theoriginway.com/">www.theoriginway.com</a> or <a href="https://www.instagram.com/theoriginway/">@theoriginway.</a>',
        shortBio:
          'Origin is the nationwide leader in pelvic floor and women’s health physical therapy, with virtual care in 50 states, 17 clinics, and 41M covered lives. We support full body needs for women and all people with vaginal anatomy through every stage of life, serving over 30,000 patients to date with an NPS of 98.'
      }
    ]
  }
];

export default { specialistsPPC, specialistsVMS };
